import React from "react";
import { Heading, Text } from "@flixbus/honeycomb-react";
import type { ChatMessage } from "./chatMessages";
import { toStatusCopy, welcomeTitle, welcomeBody } from "./chatCopy";
import ProgressBody from "./ProgressBody.component";
import FailedBody from "./FailedBody.component";
import styles from "./ChatMessage.module.scss";

type AssistantMessage = Extract<ChatMessage, { role: "assistant" }>;

export interface AssistantBodyProps {
  message: AssistantMessage;
  onRetry: () => void;
}

/** The assistant bubble's content, one branch per message kind. */
const AssistantBody: React.FC<AssistantBodyProps> = ({ message, onRetry }) => {
  switch (message.kind) {
    case "welcome":
      return (
        <>
          <Heading size={4} flushSpace>
            {welcomeTitle}
          </Heading>
          <Text extraClasses={styles.bubbleText}>{welcomeBody}</Text>
        </>
      );

    case "progress":
      return <ProgressBody status={message.status} />;

    case "ready": {
      // Success is terminal: the surface fires a success toast and closes the
      // panel, so the ready bubble carries no action button — it's the last
      // thing shown before the panel closes.
      const { job } = message;
      const total = job.parseOutcomes.length;
      const used = job.parseOutcomes.filter(
        (o) => o.status === "PARSED",
      ).length;
      return (
        <>
          <Text extraClasses={styles.bubbleText}>
            {job.summary ?? toStatusCopy("READY")}
          </Text>
          {total > 0 && (
            <Text small extraClasses={styles.assistantMeta}>
              {used} of {total} document{total !== 1 ? "s" : ""} used
            </Text>
          )}
        </>
      );
    }

    case "noData":
      // No action buttons: the conversation continues in the composer — the user
      // uploads a different document or refines the prompt in the same thread.
      return (
        <>
          <Text extraClasses={styles.bubbleText}>
            {toStatusCopy("READY_NO_DATA")}
          </Text>
          {message.job.summary && (
            <Text small extraClasses={styles.assistantMeta}>
              {message.job.summary}
            </Text>
          )}
        </>
      );

    case "failed":
      return <FailedBody message={message} onRetry={onRetry} />;
  }
};

export default React.memo(AssistantBody);
